import { css, html, nothing } from 'lit';
import '../elements/vitesse-icon';
const BTN_ICON_SIZE = { sm: 14, md: 15, lg: 16 };
/** The one reusable text button — and, with no `label`, the icon-only square
 *  variant (a trash/action affordance). Icon-only is still a real base button
 *  (same flat shell + token border), but since there's no text to carry the
 *  highlight, the ICON itself takes the variant colour on hover (e.g. a red
 *  trash) — see `.base-button-icon-only` in base-button.scss. */
export function button(o) {
    const size = o.size ?? 'md';
    const iconOnly = !o.label;
    return html `<button
    class="base-button base-button-${o.color ?? 'neutral'}${size === 'md' ? '' : ` base-button-${size}`}${iconOnly ? ' base-button-icon-only' : ''}${o.active ? ' active' : ''}${o.filled ? ' base-button-filled' : ''}"
    ?disabled=${o.disabled}
    title=${o.title ?? nothing}
    aria-label=${iconOnly ? (o.title ?? nothing) : nothing}
    aria-pressed=${o.pressed === undefined ? nothing : String(o.pressed)}
    @click=${o.onClick}
  >
    ${o.icon ? html `<vitesse-icon name=${o.icon} size=${BTN_ICON_SIZE[size]}></vitesse-icon>` : nothing}${iconOnly ? nothing : html `<span>${o.label}</span>`}
  </button>`;
}
/**
 * THE SAME BUTTON, WHEN THE ACTION IS GOING SOMEWHERE ELSE.
 *
 * An action that opens another origin in a new tab must stay an anchor: middle-click, cmd-click and
 * "copy link" are things people do to links and cannot be given back by a click handler calling
 * window.open. But the two of these in the assistant's header were hand-written anchors carrying
 * `class="base-button base-button-neutral base-button-sm"` as a string literal — which is a copy of
 * the base button rather than the base button, and drifts from it the first time the class names
 * move.
 *
 * So it is the same helper with the same options, rendering the same classes from the same code.
 * Semantically a link, visually and structurally the button.
 */
export function linkButton(o) {
    const size = o.size ?? 'md';
    const iconOnly = !o.label;
    return html `<a
    class="base-button base-button-${o.color ?? 'neutral'}${size === 'md' ? '' : ` base-button-${size}`}${iconOnly ? ' base-button-icon-only' : ''}${o.filled ? ' base-button-filled' : ''}"
    href=${o.href}
    target="_blank"
    rel="noopener noreferrer"
    title=${o.title ?? o.label ?? nothing}
    aria-label=${iconOnly ? (o.title ?? nothing) : nothing}
  >
    ${o.icon ? html `<vitesse-icon name=${o.icon} size=${BTN_ICON_SIZE[size]}></vitesse-icon>` : nothing}${iconOnly ? nothing : html `<span>${o.label}</span>`}
  </a>`;
}
/** The flat icon-only button (`.base-icon-button`) — grows + colors on hover.
 *  `disabled` greys it out and drops the hover affordances (the mixin's hovers
 *  are all :not(:disabled)); the title still shows on hover, so a disabled
 *  action carries its own explanation instead of needing a banner. */
export function iconButton(o) {
    return html `<button
    class="base-icon-button ${o.danger ? 'danger' : ''} ${o.active ? 'active' : ''}"
    title=${o.title}
    aria-label=${o.title}
    ?disabled=${o.disabled}
    @click=${o.onClick}
  >
    <vitesse-icon name=${o.icon} size=${o.size ?? 16}></vitesse-icon>
  </button>`;
}
/**
 * The `.base-button` rules as a shadow-DOM-consumable `css` — the exact mirror of
 * `styles/components/base-button.scss` (the light-DOM copy), following the same
 * dual light-SCSS / shadow-css pattern as `statusBadgeStyles` and the icon-button.
 *
 * WHY THIS HAS TO EXIST. `button()` above emits ONLY class names; every pixel of
 * its appearance lives in that global stylesheet, which is attached to the
 * document and cannot cross a shadow boundary. So a shadow-DOM Lit element that
 * renders `button()` gets bare browser buttons — no border, no sizing, an
 * unaligned icon — with no error anywhere to explain it. That has now been
 * diagnosed three separate times in the integration screens; this export is so
 * there is no fourth. A shadow-DOM component that calls `button()` MUST include
 * this in its `static styles`. A LIGHT-DOM component must not — it already
 * inherits the global sheet.
 *
 * Colours read theme custom properties, which do inherit through the boundary.
 */
export const baseButtonStyles = css `
  .base-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    height: 30px;
    padding: 0 12px;
    border: 1px solid var(--border-color);
    border-radius: 6px;
    background: var(--bg-primary);
    color: var(--text-secondary);
    font-size: 13px;
    font-weight: 500;
    font-family: inherit;
    cursor: pointer;
    white-space: nowrap;
    transition:
      color 0.15s ease,
      border-color 0.15s ease,
      background 0.15s ease;
  }
  /* The icon keeps its own neutral colour through hover — only text + border take
     the highlight. Covers raw inline SVG and the shadow-DOM <vitesse-icon>. */
  .base-button svg,
  .base-button vitesse-icon {
    flex-shrink: 0;
    color: var(--text-secondary);
  }
  .base-button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  /* highlight colour: hover and toggled-.active */
  .base-button.base-button-blue:hover:not(:disabled),
  .base-button.base-button-blue.active {
    color: var(--blue);
    border-color: var(--blue);
  }
  .base-button.base-button-red:hover:not(:disabled),
  .base-button.base-button-red.active {
    color: var(--red);
    border-color: var(--red);
  }
  .base-button.base-button-green:hover:not(:disabled),
  .base-button.base-button-green.active {
    color: var(--green);
    border-color: var(--green);
  }
  .base-button.base-button-yellow:hover:not(:disabled),
  .base-button.base-button-yellow.active {
    color: var(--yellow);
    border-color: var(--yellow);
  }
  /* Neutral hovers BLUE like every other interactive control (see the SCSS note). */
  .base-button.base-button-neutral:hover:not(:disabled) {
    color: var(--blue);
    border-color: var(--blue);
  }

  /* filled: the PRIMARY action. .active is listed so a filled toggle does not
     lose to .base-button-<colour>.active and paint accent text on accent fill. */
  .base-button.base-button-filled,
  .base-button.base-button-filled.active {
    color: var(--on-accent);
    border-color: transparent;
    font-weight: 600;
  }
  .base-button.base-button-filled:hover:not(:disabled) {
    filter: brightness(1.08);
    color: var(--on-accent);
  }
  .base-button.base-button-filled.base-button-blue {
    background: var(--blue);
  }
  .base-button.base-button-filled.base-button-red {
    background: var(--red);
  }
  .base-button.base-button-filled.base-button-green {
    background: var(--green);
  }
  .base-button.base-button-filled.base-button-yellow {
    background: var(--yellow);
  }
  .base-button.base-button-filled.base-button-neutral {
    background: var(--bg-tertiary);
    color: var(--text-primary);
  }

  /* size scale */
  .base-button.base-button-sm {
    height: 26px;
    padding: 0 9px;
    font-size: 12px;
    gap: 5px;
  }
  .base-button.base-button-lg {
    height: 36px;
    padding: 0 16px;
    font-size: 14px;
    gap: 8px;
  }

  /* icon-only: a square affordance whose ICON takes the variant colour on hover */
  .base-button.base-button-icon-only {
    width: 30px;
    padding: 0;
    gap: 0;
  }
  .base-button.base-button-icon-only svg,
  .base-button.base-button-icon-only vitesse-icon {
    color: inherit;
  }
  .base-button.base-button-icon-only.base-button-sm {
    width: 26px;
  }
  .base-button.base-button-icon-only.base-button-lg {
    width: 36px;
  }
`;
//# sourceMappingURL=base-buttons.js.map